import React, { useState, useEffect } from "react";
import { Col, Container, Row, Button, Spinner, Card, Form, Badge } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import { FiEdit3, FiArrowLeft, FiMapPin, FiTag, FiDollarSign, FiInfo } from "react-icons/fi";
import LeaseNotes from "./LeaseNotes";

const LEASE_STATUS_OPTIONS = [
  "PENDING",
  "IN_REVIEW",
  "APPROVED",
  "REJECTED",
  "COMPLETED",
];

const getStatusBadgeVariant = (status) => {
  switch (status?.toUpperCase()) {
    case "APPROVED":
    case "COMPLETED":
      return "success";
    case "IN_REVIEW":
      return "info";
    case "REJECTED":
      return "danger";
    case "PENDING":
    default:
      return "warning";
  }
};

const LeaseItem = () => {
  const navigate = useNavigate();
  const { Id } = useParams();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}/lease/${Id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const resData = response.data;
        const dataObj =
          resData?.lease ||
          resData?.data ||
          (typeof resData === "object" ? resData : null);

        setDetails(dataObj);
      } catch (error) {
        console.error("Error fetching lease details:", error);
        showToast("Error fetching lease details.", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (Id) {
      fetchDetails();
    }
  }, [Id, baseUrl, token]);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await axios.put(
        `${baseUrl}/lease/status`,
        {
          lease_id: Number(Id),
          status: newStatus,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Lease status updated successfully!", { type: "success" });
        setDetails((prev) => ({ ...prev, status: newStatus }));
      } else {
        showToast(response.data?.text || "Failed to update status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating lease status:", error);
      showToast("Error updating lease status", { type: "error" });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Lease", link: "/lease" },
    { label: details?.title || `Lease #${Id}`, link: `/lease/${Id}`, active: true },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading details...</p>
      </div>
    );
  }

  const purposeName =
    details?.purpose?.name || details?.purpose_name || details?.purpose || "-";
  const locationText =
    [details?.city, details?.state].filter(Boolean).join(", ") || "-";
  const formattedBudget = details?.budget
    ? `₹ ${Number(details.budget).toLocaleString("en-IN")}`
    : "-";

  return (
    <>
      <Notification />
      <div className="content-body2">
        <Container>
          <BreadCrumb items={breadcrumbItems} />

          {/* Header Card */}
          <Card className="shadow-sm border-0 rounded-3 mt-3 overflow-hidden">
            <Card.Body className="p-4 bg-white">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h3 className="mb-0 fw-bold text-dark">
                      {details?.title || "Lease Request"}
                    </h3>
                    <Badge
                      bg={getStatusBadgeVariant(details?.status)}
                      className="px-2 py-1 text-uppercase"
                      style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                    >
                      {details?.status || "PENDING"}
                    </Badge>
                  </div>
                  <small className="text-muted">Lease Record ID: #{details?.id || Id}</small>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/lease")}
                  >
                    <FiArrowLeft className="me-1" /> Back to Leases
                  </Button>
                  <Link
                    to={`/lease/${Id}/edit`}
                    className="btn btn-primary btn-sm d-flex align-items-center"
                  >
                    <FiEdit3 className="me-1" /> Edit Lease
                  </Link>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Details Overview Cards */}
          <Row className="g-3 mt-1">
            <Col md={4}>
              <Card className="shadow-sm border-0 rounded-3 h-100 p-3 bg-white">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3 text-primary"
                    style={{ backgroundColor: "#eef2ff" }}
                  >
                    <FiTag size={22} />
                  </div>
                  <div>
                    <small className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: "11px" }}>
                      Lease Purpose
                    </small>
                    <span className="fw-bold text-dark fs-6">{purposeName}</span>
                  </div>
                </div>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="shadow-sm border-0 rounded-3 h-100 p-3 bg-white">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3 text-success"
                    style={{ backgroundColor: "#ecfdf5" }}
                  >
                    <FiDollarSign size={22} />
                  </div>
                  <div>
                    <small className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: "11px" }}>
                      Budget
                    </small>
                    <span className="fw-bold text-dark fs-6">{formattedBudget}</span>
                  </div>
                </div>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="shadow-sm border-0 rounded-3 h-100 p-3 bg-white">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3 text-danger"
                    style={{ backgroundColor: "#fef2f2" }}
                  >
                    <FiMapPin size={22} />
                  </div>
                  <div>
                    <small className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: "11px" }}>
                      Location
                    </small>
                    <span className="fw-bold text-dark fs-6">{locationText}</span>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Additional Information & Status Update Section */}
          <Card className="shadow-sm border-0 rounded-3 mt-3 p-4 bg-white">
            <h5 className="fw-bold text-dark mb-3 d-flex align-items-center">
              <FiInfo className="me-2 text-primary" /> Lease Information
            </h5>

            <Row className="g-3">
              <Col md={6}>
                <div className="p-3 rounded bg-light border">
                  <small className="text-muted d-block mb-1 font-monospace">DESCRIPTION</small>
                  <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {details?.description || "No description provided."}
                  </p>
                </div>
              </Col>

              <Col md={6}>
                <div className="p-3 rounded bg-light border h-100 d-flex flex-column justify-content-center">
                  <small className="text-muted d-block mb-2 font-monospace">MANAGEMENT STATUS</small>
                  <div className="d-flex align-items-center gap-3">
                    <Form.Select
                      size="sm"
                      value={details?.status || "PENDING"}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      className="fw-bold border-primary"
                      style={{ width: "200px" }}
                    >
                      {LEASE_STATUS_OPTIONS.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </Form.Select>
                    {updatingStatus && <Spinner animation="border" size="sm" variant="primary" />}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Lease Notes Component */}
          <LeaseNotes leaseId={Id} />
        </Container>
      </div>
    </>
  );
};

export default LeaseItem;
